/**
 * DocumentPreviewBody — the preview itself, without any chrome around it.
 *
 * Shared by DocumentPreviewDrawer (Brief 91 finder, Bundle Builder) and the
 * matter page's documents pane (BM-H4266). See DocumentPreviewDrawer for why
 * there are several "tiers" and why a failed iframe advances to the next one.
 *
 * Two strategies:
 *
 *  - "bundle" (the drawer, unchanged): the backend-rendered PDF first, since
 *    that is exactly what a bundle will carry, then SharePoint's own tiers.
 *
 *  - "matter" (BM-H4266, the matter page): the quickest faithful view for the
 *    kind of file. Word, Excel and PowerPoint open in SharePoint's own viewer
 *    (the rendered PDF loses their layout), falling back to the rendered PDF
 *    if SharePoint's viewer will not load. Emails, PDFs, images and text use
 *    the rendered PDF first, because SharePoint cannot show a saved email at
 *    all. A SharePoint download link is only iframed for a PDF or an image:
 *    for anything else the browser would save the file to Downloads instead
 *    of showing it, which is the opposite of a preview.
 *
 * Read only. Every call here is a read of the document; nothing is written.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Loader2, ExternalLink, AlertTriangle } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

// If an iframe hasn't fired onLoad within this window, treat it as failed
// and advance to the next tier. Some SharePoint failure pages render fully
// (same-origin "File Not Found") without ever firing onError, so onLoad
// alone can't be trusted as success either — but a working preview loads
// in well under this window, so a hung/blank frame past it is not one.
export const TIER_LOAD_TIMEOUT_MS = 6000;

const OFFICE_EXT = /\.(docx?|xlsx?|xlsm|pptx?)$/i;
const INLINE_DOWNLOAD_EXT = /\.(pdf|png|jpe?g|gif|bmp|webp)$/i;

export function isOfficeFile(name) {
  return OFFICE_EXT.test(name || "");
}

async function fetchRendered(itemId) {
  const rendered = await axios.post(
    `${API}/documents/preview/rendered`,
    { item_id: itemId },
    { responseType: "blob" },
  );
  if (rendered && rendered.data instanceof Blob && rendered.data.size > 0) {
    return URL.createObjectURL(new Blob([rendered.data], { type: "application/pdf" }));
  }
  return null;
}

export default function DocumentPreviewBody({ doc, strategy = "bundle", renderedNote }) {
  const [tiers, setTiers] = useState([]);
  const [tierIndex, setTierIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [iframeKey, setIframeKey] = useState(0);
  const timeoutRef = useRef(null);
  const iframeNodeRef = useRef(null);
  const renderedUrlRef = useRef(null);
  const loadSeq = useRef(0);

  const releaseRendered = () => {
    if (renderedUrlRef.current) {
      URL.revokeObjectURL(renderedUrlRef.current);
      renderedUrlRef.current = null;
    }
  };

  // Release the rendered PDF when the body unmounts.
  useEffect(() => () => releaseRendered(), []);

  const loadPreview = useCallback(async (itemId, name) => {
    const seq = ++loadSeq.current;
    const stale = () => seq !== loadSeq.current;
    setLoading(true);
    setError(null);
    setTiers([]);
    setTierIndex(0);
    releaseRendered();

    const office = strategy === "matter" && isOfficeFile(name);

    if (!office) {
      try {
        const url = await fetchRendered(itemId);
        if (stale()) { if (url) URL.revokeObjectURL(url); return; }
        if (url) {
          renderedUrlRef.current = url;
          setTiers([{ preview_url: url, type: "rendered" }]);
          setLoading(false);
          return;
        }
      } catch (err) {
        // Fall through to SharePoint's own preview tiers.
      }
    }
    try {
      const res = await axios.post(`${API}/documents/preview`, { item_id: itemId });
      if (stale()) return;
      let nextTiers = res.data?.tiers || [];
      if (strategy === "matter") {
        if (!INLINE_DOWNLOAD_EXT.test(name || "")) {
          nextTiers = nextTiers.filter((t) => t.type !== "download");
        }
        if (office) {
          // SharePoint's viewer first; if it will not load, the rendered PDF,
          // then the Open in SharePoint panel.
          const web = nextTiers.filter((t) => t.type === "web");
          nextTiers = [
            ...nextTiers.filter((t) => t.type !== "web"),
            { preview_url: null, type: "render-request" },
            ...web,
          ];
        }
      }
      setTiers(nextTiers);
      if (nextTiers.length === 0) setError("No preview available for this document.");
    } catch (err) {
      if (stale()) return;
      if (office) {
        // SharePoint's preview call failed outright: try the rendered PDF.
        setTiers([{ preview_url: null, type: "render-request" }]);
      } else {
        setError(errorMessage(err, "Could not load preview."));
      }
    } finally {
      if (!stale()) setLoading(false);
    }
  }, [strategy]);

  useEffect(() => {
    if (doc?.id) loadPreview(doc.id, doc.title || doc.name);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [doc, loadPreview]);

  const advanceTier = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setTierIndex((i) => i + 1);
    setIframeKey((k) => k + 1);
  }, []);

  const activeTier = tiers[tierIndex];
  const exhausted = tiers.length > 0 && tierIndex >= tiers.length;

  // A "render-request" tier asks the backend for the rendered PDF only once
  // SharePoint's own viewer has failed (Office files on the matter page).
  useEffect(() => {
    if (!activeTier || activeTier.type !== "render-request" || !doc?.id) return undefined;
    let cancelled = false;
    fetchRendered(doc.id)
      .then((url) => {
        if (cancelled) { if (url) URL.revokeObjectURL(url); return; }
        if (!url) { advanceTier(); return; }
        releaseRendered();
        renderedUrlRef.current = url;
        setTiers((ts) => ts.map((t, i) => (i === tierIndex ? { preview_url: url, type: "rendered" } : t)));
      })
      .catch(() => { if (!cancelled) advanceTier(); });
    return () => { cancelled = true; };
  }, [activeTier, tierIndex, doc, advanceTier]);

  const pendingRender = activeTier && activeTier.type === "render-request";

  // Only Tier 1/3 ("embed") and Tier 2 ("download") are worth iframing.
  // Tier 4 ("web", the bare SharePoint webUrl) is a full page that will
  // refuse to render in an iframe for auth reasons — show the fallback
  // panel with an Open button instead of wasting a load attempt on it.
  const canIframe = activeTier && activeTier.type !== "web" && !pendingRender;

  // A rendered PDF is a local blob: it cannot be a SharePoint error page, and
  // the browser's PDF viewer does not always fire load on it, so it is never
  // timed out into the next tier.
  const isRendered = activeTier && activeTier.type === "rendered";

  useEffect(() => {
    if (!canIframe || isRendered) return undefined;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      advanceTier();
    }, TIER_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierIndex, canIframe, isRendered, iframeKey]);

  const handleIframeLoad = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const handleIframeError = useCallback(() => {
    advanceTier();
  }, [advanceTier]);

  // React's synthetic event system does not reliably delegate `error` for
  // <iframe> (it only auto-attaches native listeners for a fixed "media"
  // element list — img/video/audio — not iframe), so onError as a JSX prop
  // can silently never fire. Bind the native listener directly to the node
  // instead. A callback ref fires precisely when React attaches/detaches the
  // DOM node (the iframe mounts late and remounts on every tier advance via
  // `key`), so the listeners always land on the live node.
  const setIframeRef = useCallback((node) => {
    const prev = iframeNodeRef.current;
    if (prev) {
      prev.removeEventListener("load", handleIframeLoad);
      prev.removeEventListener("error", handleIframeError);
    }
    iframeNodeRef.current = node;
    if (node) {
      node.addEventListener("load", handleIframeLoad);
      node.addEventListener("error", handleIframeError);
    }
  }, [handleIframeLoad, handleIframeError]);

  return (
    <div className="flex-1 min-h-0 bg-slate-900 flex flex-col">
      {(loading || pendingRender) && (
        <div className="h-full flex items-center justify-center text-slate-400" data-testid="doc-preview-loading">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading preview…
        </div>
      )}
      {!loading && error && (
        <div className="h-full flex flex-col items-center justify-center text-slate-400 px-6 text-center">
          <AlertTriangle className="text-amber-400 mb-2" size={24} />
          <div className="text-sm" data-testid="doc-preview-error">{error}</div>
        </div>
      )}
      {!loading && !error && exhausted && (
        <div
          className="h-full flex flex-col items-center justify-center text-slate-300 px-6 text-center"
          data-testid="doc-preview-exhausted"
        >
          <AlertTriangle className="text-amber-400 mb-3" size={28} />
          <div className="text-sm mb-2">This file doesn't preview inline.</div>
          <div className="text-xs text-slate-400 mb-4 max-w-sm">
            SharePoint's own preview is unreliable for some file types —
            Open it directly instead.
          </div>
          {doc.web_url && (
            <a
              href={doc.web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs
                bg-teal text-navy-card hover:bg-teal-light"
            >
              <ExternalLink size={13} /> Open in SharePoint
            </a>
          )}
        </div>
      )}
      {!loading && !error && !exhausted && isRendered && renderedNote && (
        <div
          className="px-4 py-1.5 text-xs text-slate-300 border-b border-navy-border"
          data-testid="doc-preview-rendered-note"
        >
          {renderedNote}
        </div>
      )}
      {!loading && !error && !exhausted && activeTier && canIframe && (
        <iframe
          key={iframeKey}
          ref={setIframeRef}
          title="Document preview"
          data-testid="doc-preview-iframe"
          data-tier={activeTier.type}
          src={activeTier.preview_url}
          className="w-full flex-1 min-h-0 border-0 bg-white"
        />
      )}
      {!loading && !error && !exhausted && activeTier && activeTier.type === "web" && (
        <div
          className="h-full flex flex-col items-center justify-center text-slate-300 px-6 text-center"
          data-testid="doc-preview-web-fallback"
        >
          <ExternalLink className="text-teal mb-3" size={28} />
          <div className="text-sm mb-4">Opens in SharePoint.</div>
          <a
            href={activeTier.preview_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs
              bg-teal text-navy-card hover:bg-teal-light"
          >
            <ExternalLink size={13} /> Open in SharePoint
          </a>
        </div>
      )}
    </div>
  );
}
